import { showToast } from "@certn/cascadia";
import {
  ActionList,
  ActionListItem,
  GhostIconButton,
  Icon,
  Link,
  LinkButton,
  Loader,
} from "@certn/cascadia-react";
import { getOriginalFileName } from "@certn/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { InformationRequestDocument } from "./types";
import { openDocument } from "./uploads";

type Props = {
  caseId: string;
  document: InformationRequestDocument;
  isRemoving?: boolean;
  /** Without it the row offers to open the document instead of removing it. */
  onRemove?: (document: InformationRequestDocument) => void;
};

export function DocumentRow({
  caseId,
  document,
  isRemoving = false,
  onRemove,
}: Props) {
  const { t } = useTranslation();
  const fileName = getOriginalFileName(document.display_name);
  const [isOpening, setIsOpening] = useState(false);

  const handleOpen = async () => {
    setIsOpening(true);
    try {
      await openDocument(caseId, document.id);
    } catch {
      showToast(t("We couldn't open this document. Please try again."), {
        type: "error",
      });
    } finally {
      setIsOpening(false);
    }
  };

  const trailing = onRemove ? (
    !isRemoving && (
      <Link
        testID={`information-request-document-${document.id}-remove`}
        textColor="text-weak"
        textStyle="global/body-secondary"
        onPress={() => onRemove(document)}
      >
        {t("Remove")}
      </Link>
    )
  ) : (
    <GhostIconButton
      testID={`information-request-document-${document.id}-open-in-new`}
      color="icon-base"
      icon="open_in_new"
      size="small"
      isDisabled={isOpening}
      onPress={handleOpen}
      aria-label={t("Open {{filename}}", {
        filename: fileName,
      })}
    />
  );

  return (
    <ActionList>
      <ActionListItem
        alignItems="center"
        centerAlignTrailing
        testID={`information-request-document-${document.id}`}
        leading={
          isRemoving ? (
            <Loader type="spinner" size="small" />
          ) : (
            <Icon
              type="standard"
              name="description"
              color="background-accent-blue-strong"
              size="space-6"
            />
          )
        }
        title={
          <LinkButton
            testID={`information-request-document-${document.id}-open`}
            textStyle="global/heading-medium"
            textColor="text-base"
            linkStyle="monochrome"
            isLoading={isOpening}
            onPress={handleOpen}
          >
            {fileName}
          </LinkButton>
        }
        trailing={trailing}
      />
    </ActionList>
  );
}
